import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBackNavigation } from '@/hooks/use-back-navigation';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  ArrowLeft,
  Plus,
  Search,
  Trash2,
  Ticket,
  Download,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { authenticatedFetch } from '@/lib/api';
import { CustomDatePicker } from '@/components/CustomDatePicker';
import { downloadExcel } from '@/lib/excelDownload';
import { AppBar } from '@/components/ui/app-bar';

// --- Types ---
export type CouponStatus = 'Active' | 'New' | 'Expired' | 'Used' | 'Particularly used';

export interface Coupon {
  id: number;
  couponCode: string;
  amount: number;
  minBillValue: number;
  validFrom: string;
  validTo: string;
  usageLimit: number;
  usedCount: number;
  status: CouponStatus;
}

// API GetCoupons response: id, Coupon, Amount, BillValue, Valid From, Valid To, Usages, Used, Status
function normalizeCoupon(raw: Record<string, unknown>): Coupon {
  const id = Number(raw.id ?? raw.Id ?? 0);
  const couponCode = String(raw.Coupon ?? raw.CouponNo ?? raw.CouponCode ?? '');
  const amount = Number(raw.Amount ?? raw.amount ?? 0);
  const minBillValue = Number(raw.BillValue ?? raw.MinBillValue ?? raw.minBillValue ?? 0);
  const validFrom = String(raw['Valid From'] ?? raw.ValidFrom ?? raw.validFrom ?? '');
  const validTo = String(raw['Valid To'] ?? raw.ValidTo ?? raw.validTo ?? '');
  const usageLimit = Number(raw.Usages ?? raw.UsageTimes ?? raw.UsageLimit ?? 0);
  const usedCount = Number(raw.Used ?? raw.UsedCount ?? raw.usedCount ?? 0);
  const status = (raw.Status ?? raw.status ?? 'Active') as CouponStatus;
  return {
    id,
    couponCode,
    amount,
    minBillValue,
    validFrom,
    validTo,
    usageLimit,
    usedCount,
    status,
  };
}

function normalizeListResponse(raw: unknown): Coupon[] {
  if (Array.isArray(raw)) return raw.map((r) => normalizeCoupon(r as Record<string, unknown>));
  if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as { data: unknown[] }).data)) {
    return ((raw as { data: unknown[] }).data).map((r) => normalizeCoupon(r as Record<string, unknown>));
  }
  return [];
}

function getDisplayStatus(c: Coupon): CouponStatus {
  if (c.usedCount > 0) {
    return c.usedCount === c.usageLimit ? 'Used' : 'Particularly used';
  }
  return c.status;
}

function getStatusVariant(status: CouponStatus): 'success' | 'secondary' | 'destructive' {
  if (status === 'Active') return 'success';
  if (status === 'New') return 'secondary';
  if (status === 'Used' || status === 'Particularly used') return 'secondary';
  return 'destructive';
}

function getDaysUntilExpiry(validTo: string): number | null {
  if (!validTo) return null;
  const to = new Date(validTo);
  const now = new Date();
  const diff = to.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

const PAGE_SIZE = 10;

export default function CouponsManagementPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CouponStatus | 'All'>('All');
  const [sortKey, setSortKey] = useState<keyof Coupon>('id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [form, setForm] = useState({
    couponCode: '',
    amount: '0',
    minBillValue: '0',
    validFrom: new Date(),
    validTo: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    usageLimit: '0',
  });

  const fetchCoupons = useCallback(async (searchTerm = '') => {
    setLoading(true);
    const q = searchTerm.trim() ? encodeURIComponent(searchTerm.trim()) : '';
    const url = `/api/GetCoupons?coupon=${q}`;
    try {
      const res = await authenticatedFetch<unknown>(url);
      console.log('API: GetCoupons', { request: { url }, response: res.data });
      setCoupons(normalizeListResponse(res.data));
    } catch (e) {
      console.error(e);
      toast.error('Failed to load coupons');
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchCoupons(search), 300);
    return () => clearTimeout(t);
  }, [fetchCoupons, search]);

  useBackNavigation({ onBack: () => navigate('/?tab=administrator'), showExitConfirmation: false });

  const filtered = useMemo(() => {
    let list = coupons.filter((c) => {
      const displayStatus = getDisplayStatus(c);
      const matchStatus = statusFilter === 'All' || displayStatus === statusFilter;
      return matchStatus;
    });
    list = [...list].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return 0;
    });
    return list;
  }, [coupons, search, statusFilter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = useMemo(
    () => filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [filtered, page]
  );

  const handleSort = (key: keyof Coupon) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else setSortKey(key);
  };

  const handleGenerateCode = useCallback(async () => {
    setGenerating(true);
    try {
      const res = await authenticatedFetch<{ status?: number; message?: string; data?: { Coupon?: string }[] }>('/api/GenerateCoupon');
      const apiRes = res.data;
      const apiStatus = apiRes?.status ?? -1;
      const apiMessage = apiRes?.message ?? '';
      if (apiStatus === 0) {
        const coupon = Array.isArray(apiRes?.data) && apiRes.data[0]?.Coupon
          ? apiRes.data[0].Coupon
          : '';
        if (coupon) setForm((f) => ({ ...f, couponCode: coupon }));
      } else {
        toast.error(apiMessage || 'Failed to generate coupon');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to generate coupon');
    } finally {
      setGenerating(false);
    }
  }, []);

  useEffect(() => {
    if (addSheetOpen) handleGenerateCode();
  }, [addSheetOpen, handleGenerateCode]);

  const handleAddCoupon = async () => {
    const code = form.couponCode.trim();
    const amount = parseFloat(form.amount);
    const minBill = parseFloat(form.minBillValue);
    const usageLimit = parseInt(form.usageLimit, 10);
    if (!code) {
      toast.error('Enter coupon code');
      return;
    }
    if (isNaN(amount) || amount < 0) {
      toast.error('Enter valid discount amount');
      return;
    }
    const validFrom = format(form.validFrom, 'yyyy-MM-dd');
    const validTo = format(form.validTo, 'yyyy-MM-dd');
    const body = {
      coupon: code,
      usages: isNaN(usageLimit) ? 0 : usageLimit,
      amount: isNaN(amount) ? 0 : amount,
      billValue: isNaN(minBill) ? 0 : minBill,
      validFrom,
      validTo,
    };
    setSubmitting(true);
    try {
      const res = await authenticatedFetch<{ status?: number; message?: string }>('/api/CreateCoupon', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      console.log('API: CreateCoupon', { request: { body }, response: res.data });
      const apiRes = res.data as { status?: number; message?: string };
      const apiStatus = apiRes?.status ?? -1;
      const apiMessage = apiRes?.message ?? 'Unknown response';
      if (apiStatus === 0) {
        toast.success(apiMessage);
        setAddSheetOpen(false);
        setForm({
          couponCode: '',
          amount: '0',
          minBillValue: '0',
          validFrom: new Date(),
          validTo: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          usageLimit: '0',
        });
        fetchCoupons(search);
      } else {
        toast.error(apiMessage);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to add coupon');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (couponCode: string) => {
    setSubmitting(true);
    const body = { coupon: couponCode };
    try {
      const res = await authenticatedFetch<{ status?: number; message?: string }>('/api/DeleteCoupon', {
        method: 'DELETE',
        body: JSON.stringify(body),
      });
      const apiRes = res.data;
      const apiStatus = apiRes?.status ?? -1;
      const apiMessage = apiRes?.message ?? '';
      if (apiStatus === 0) {
        toast.success(apiMessage);
        setCouponToDelete(null);
        fetchCoupons(search);
      } else {
        toast.error(apiMessage || 'Failed to delete coupon');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete coupon');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = () => {
    const rows = filtered.map((c) => ({
      'Coupon Code': c.couponCode,
      Amount: c.amount,
      'Min Bill Value': c.minBillValue,
      'Valid From': c.validFrom ? format(new Date(c.validFrom), 'yyyy-MM-dd') : '',
      'Valid To': c.validTo ? format(new Date(c.validTo), 'yyyy-MM-dd') : '',
      'Usage Limit': c.usageLimit,
      'Used Count': c.usedCount,
      Status: getDisplayStatus(c),
    }));
    downloadExcel(rows, 'Coupons', 'Coupons', () => toast.success('Export started'), (err) => toast.error(err));
  };

  const StatusBadge = ({ status }: { status: CouponStatus }) => (
    <Badge variant={getStatusVariant(status)} className={cn(status === 'New' && 'bg-blue-100 text-blue-800 border-blue-200')}>
      {status}
    </Badge>
  );

  const ExpiryWarning = ({ validTo }: { validTo: string }) => {
    const days = getDaysUntilExpiry(validTo);
    if (days == null || days > 3 || days < 0) return null;
    return (
      <span className="text-amber-600 text-xs flex items-center gap-1 mt-1">
        <AlertTriangle className="h-3.5 w-3.5" />
        {days < 0 ? 'Expired' : `Expires in ${days} day${days === 1 ? '' : 's'}`}
      </span>
    );
  };

  const emptyIllustration = (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        <Ticket className="w-10 h-10 text-gray-400" />
      </div>
      <p className="text-gray-600 font-medium">No coupons yet</p>
      <p className="text-sm text-gray-500 mt-1">Create your first discount coupon to get started.</p>
      <Button className="mt-4 bg-teal-600 hover:bg-teal-700" onClick={() => (isMobile ? setAddSheetOpen(true) : setAddSheetOpen(true))}>
        <Plus className="h-4 w-4 mr-2" />
        Add Coupon
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Mobile: App bar (no buttons in header) */}
      <div className="lg:hidden flex-shrink-0">
        <AppBar title="Coupons" onBack={() => navigate('/?tab=administrator')} showBackButton />
      </div>

      <div className="p-4 pt-14 md:p-6 lg:p-8 lg:pt-8 max-w-7xl mx-auto space-y-4 md:space-y-6">
        {/* Header: title + actions */}
        <div className="hidden lg:flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-gray-600" onClick={() => navigate('/?tab=administrator')} aria-label="Back to Administration">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Coupons</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setAddSheetOpen(true)} className="bg-teal-600 hover:bg-teal-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Coupon
            </Button>
            <Button variant="outline" onClick={handleExport} disabled={filtered.length === 0}>
              <Download className="h-4 w-4 mr-2" />
              Export to Excel
            </Button>
          </div>
        </div>

        {/* Mobile: search + Add beside it, Export below, status dropdown */}
        <div className="lg:hidden space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by code or status..."
                className="pl-9"
              />
            </div>
            <Button onClick={() => setAddSheetOpen(true)} className="bg-teal-600 hover:bg-teal-700 flex-shrink-0">
              <Plus className="h-4 w-4 mr-1.5 lg:mr-2" />
              Add
            </Button>
          </div>
          <Button variant="outline" className="w-full" onClick={handleExport} disabled={filtered.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            Export to Excel
          </Button>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All</SelectItem>
              <SelectItem value="New">New</SelectItem>
              <SelectItem value="Particularly used">Particularly used</SelectItem>
              <SelectItem value="Used">Used</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Desktop: search + filter buttons */}
        <div className="hidden lg:flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by code or status..."
              className="pl-9"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {(['All', 'New', 'Particularly used', 'Used'] as const).map((s) => (
              <Button
                key={s}
                variant={statusFilter === s ? 'default' : 'outline'}
                size="sm"
                className={statusFilter === s ? 'bg-teal-600 hover:bg-teal-700' : ''}
                onClick={() => setStatusFilter(s)}
              >
                {s}
              </Button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden min-h-[200px]">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isMobile ? (
            /* Mobile: card list */
            <ScrollArea className="h-[60vh] md:h-[70vh]">
              <div className="p-4 space-y-4 pb-24">
                {paginated.length === 0
                  ? emptyIllustration
                  : paginated.map((c) => (
                      <Card key={c.id} className="rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <CardContent className="p-4 space-y-3">
                          <div className="flex justify-between items-start">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">S.NO: {c.id}</span>
                                <span className="font-semibold text-gray-900 truncate">{c.couponCode}</span>
                              </div>
                            </div>
                            <StatusBadge status={getDisplayStatus(c)} />
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                            <span>Discount: ₹{c.amount.toFixed(2)}</span>
                            <span>Min bill: ₹{c.minBillValue.toFixed(2)}</span>
                            <span>Limit: {c.usageLimit}</span>
                            <span>Used: {c.usedCount}</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {c.validFrom ? format(new Date(c.validFrom), 'dd MMM yyyy') : '—'} →{' '}
                            {c.validTo ? format(new Date(c.validTo), 'dd MMM yyyy') : '—'}
                          </div>
                          <ExpiryWarning validTo={c.validTo} />
                          {c.status === 'New' && (
                            <div className="flex gap-2 pt-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => setCouponToDelete(c.couponCode)}
                              >
                                <Trash2 className="h-4 w-4 mr-1" /> Delete
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
              </div>
            </ScrollArea>
          ) : (
            /* Desktop: table */
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-white border-b shadow-sm">
                    <TableRow className="bg-gray-50/80 hover:bg-gray-50/80 border-b">
                      <TableHead><button type="button" className="font-semibold text-left" onClick={() => handleSort('id')}>S.NO</button></TableHead>
                      <TableHead><button type="button" className="font-semibold text-left" onClick={() => handleSort('couponCode')}>Coupon Code</button></TableHead>
                      <TableHead><button type="button" className="font-semibold text-left" onClick={() => handleSort('amount')}>Amount</button></TableHead>
                      <TableHead><button type="button" className="font-semibold text-left" onClick={() => handleSort('minBillValue')}>Min Bill Value</button></TableHead>
                      <TableHead><button type="button" className="font-semibold text-left" onClick={() => handleSort('validFrom')}>Valid From</button></TableHead>
                      <TableHead><button type="button" className="font-semibold text-left" onClick={() => handleSort('validTo')}>Valid To</button></TableHead>
                      <TableHead><button type="button" className="font-semibold text-left" onClick={() => handleSort('usageLimit')}>Usage Limit</button></TableHead>
                      <TableHead>Used Count</TableHead>
                      <TableHead><button type="button" className="font-semibold text-left" onClick={() => handleSort('status')}>Status</button></TableHead>
                      <TableHead className="w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginated.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="h-48">
                          {emptyIllustration}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginated.map((c) => (
                        <TableRow key={c.id} className="hover:bg-gray-50/50">
                          <TableCell className="text-gray-600">{c.id}</TableCell>
                          <TableCell className="font-medium">{c.couponCode}</TableCell>
                          <TableCell>₹{c.amount.toFixed(2)}</TableCell>
                          <TableCell>₹{c.minBillValue.toFixed(2)}</TableCell>
                          <TableCell>{c.validFrom ? format(new Date(c.validFrom), 'dd MMM yyyy') : '—'}</TableCell>
                          <TableCell>
                            {c.validTo ? format(new Date(c.validTo), 'dd MMM yyyy') : '—'}
                            <ExpiryWarning validTo={c.validTo} />
                          </TableCell>
                          <TableCell>{c.usageLimit}</TableCell>
                          <TableCell>{c.usedCount}</TableCell>
                          <TableCell><StatusBadge status={getDisplayStatus(c)} /></TableCell>
                          <TableCell>
                            {c.status === 'New' && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => setCouponToDelete(c.couponCode)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2">
                  <p className="text-sm text-gray-500">
                    Page {page + 1} of {totalPages} ({filtered.length} coupons)
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                      Previous
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile: Add coupon sheet */}
      <Sheet open={addSheetOpen} onOpenChange={setAddSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[90vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Add coupon</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 pt-4 pb-8">
            <div className="space-y-2">
              <Label>Coupon Code</Label>
              <div className="flex gap-2">
                <Input
                  value={form.couponCode}
                  onChange={(e) => setForm((f) => ({ ...f, couponCode: e.target.value }))}
                  placeholder=""
                  className="flex-1 text-base min-h-[48px]"
                />
                <Button type="button" variant="outline" onClick={handleGenerateCode} disabled={generating} className="min-h-[48px]">
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Generate'}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Discount Amount</Label>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                className="min-h-[48px] text-base"
              />
            </div>
            <div className="space-y-2">
              <Label>Minimum Bill Value</Label>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={form.minBillValue}
                onChange={(e) => setForm((f) => ({ ...f, minBillValue: e.target.value }))}
                className="min-h-[48px] text-base"
              />
            </div>
            <div className="space-y-2">
              <Label>Valid From</Label>
              <CustomDatePicker selectedDate={form.validFrom} onChange={(d) => setForm((f) => ({ ...f, validFrom: d }))} />
            </div>
            <div className="space-y-2">
              <Label>Valid To</Label>
              <CustomDatePicker selectedDate={form.validTo} onChange={(d) => setForm((f) => ({ ...f, validTo: d }))} />
            </div>
            <div className="space-y-2">
              <Label>Usage Times</Label>
              <Input
                type="number"
                min={0}
                value={form.usageLimit}
                onChange={(e) => setForm((f) => ({ ...f, usageLimit: e.target.value }))}
                className="min-h-[48px] text-base"
              />
            </div>
            <Button className="w-full bg-teal-600 hover:bg-teal-700 min-h-[48px] text-base" onClick={handleAddCoupon} disabled={submitting}>
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Add Coupon'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete confirmation */}
      <AlertDialog open={couponToDelete != null} onOpenChange={(open) => !open && setCouponToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete coupon?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => couponToDelete != null && handleDelete(couponToDelete)} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
